import {BaseLoadingStore} from "app/modules/common/stores/BaseLoadingStore";
import {action, makeObservable, observable} from "mobx";
import {FieldState, FormState} from "formstate";
import {email, required, requiredEditor} from "app/modules/common/form/validators";
import {inject} from "react-ioc";
import {ApiClients} from "app/modules/company/services/AxiosBaseClient";
import {MainStore} from "app/modules/company/stores/MainStore";
import {CompanyIntegrationType, EmailBidSettingParam, EmailBidSettings, SendTestBidEmailModel} from "app/modules/company/api/clients.api";
import {ContentState, EditorState} from 'draft-js';

function extractDomainFromEmail(emailStr: string): string | null {
    const trimmed = (emailStr || "").trim().toLowerCase();
    const at = trimmed.indexOf("@");
    if (at === -1 || at === trimmed.length - 1) return null;
    return trimmed.slice(at + 1).trim() || null;
}

export class BidEmailDetailsStore extends BaseLoadingStore {
    @inject companyApiClients: ApiClients;
    @inject mainStore: MainStore;

    title: string = 'Email Bid Setting';

    params: EmailBidSettingParam[] = [];
    verifiedDomains: string[] = [];
    isVerifiedSmtpSender: boolean = false;

    formEmailBidSettingsState: FormState<{
        subjectTemplate: FieldState<string>;
        bodyTemplate: FieldState<EditorState>;
        fromDisplayTemplate: FieldState<string>;
        fromEmail: FieldState<string | null>;
        ccEmail: FieldState<string | null>;
    }>;

    constructor() {
        super();

        const self = this;
        const verifiedDomainValidator = (value: string | null | undefined): string | null => {
            if (!self.isVerifiedSmtpSender || !self.verifiedDomains?.length) return null;
            if (!value || !String(value).trim()) return null;
            const domain = extractDomainFromEmail(String(value).trim());
            if (!domain) return "Invalid email";
            const normalized = self.verifiedDomains.map(d => (d || "").trim().toLowerCase());
            if (!normalized.includes(domain)) {
                return "From Email domain must be one of the tenant verified domains: " + self.verifiedDomains.join(", ");
            }
            return null;
        };

        this.formEmailBidSettingsState = new FormState({
            subjectTemplate: new FieldState('').validators(required),
            bodyTemplate: new FieldState(EditorState.createEmpty()).validators(requiredEditor),
            fromDisplayTemplate: new FieldState('').validators(required),
            fromEmail: new FieldState<string | null>(null).validators(required, email, verifiedDomainValidator),
            ccEmail: new FieldState(null).validators(email),
        });

        makeObservable(this, {
            title: observable,
            verifiedDomains: observable,
            isVerifiedSmtpSender: observable,

            init: action,
            saveSettings: action
        });
    }

    saveSettings = async () => {
        await this.formEmailBidSettingsState.validate();

        if(this.formEmailBidSettingsState.hasError) {
            return;
        }

        await this.wrapLoadingApiCall(async () => {
            await this.companyApiClients.settingsClient.setEmailBidSettings(new EmailBidSettings({
                subjectTemplate: this.formEmailBidSettingsState.$.subjectTemplate.value,
                bodyTemplate: this.formEmailBidSettingsState.$.bodyTemplate.value.getCurrentContent().getPlainText("\n"),
                fromDisplayTemplate: this.formEmailBidSettingsState.$.fromDisplayTemplate.value,
                fromEmail: this.formEmailBidSettingsState.$.fromEmail.value,
                cCEmail: this.formEmailBidSettingsState.$.ccEmail.value,
            }));
        });
    }

    sendTestEmail = async () => {
        await this.formEmailBidSettingsState.validate();

        if(this.formEmailBidSettingsState.hasError) {
            return;
        }

        await this.wrapLoadingApiCall(async () => {
            await this.companyApiClients.settingsClient.testEmail(new SendTestBidEmailModel({
                subject: this.formEmailBidSettingsState.$.subjectTemplate.value,
                body: this.formEmailBidSettingsState.$.bodyTemplate.value.getCurrentContent().getPlainText("\n"),
                fromDisplay: this.formEmailBidSettingsState.$.fromDisplayTemplate.value
            }));
        });
    }

    replaceParams = (value: string) => {
        let val = value?.toString() || "";

        for(let i = 0; i < this.params.length; i++) {
            if(this.params[i].param == "[CompanyLogo]") {
                val = val.split(this.params[i].param).join(`<img src='${this.params[i].example}'/>`);
            } else {
                val = val.split(this.params[i].param).join(this.params[i].example);
            }


        }

        return val;
    }

    init = async () => {
        await this.wrapInitLoadingApiCall(async () => {
            const [settings, params, bidSmtp, verifiedDomains] = [
                await this.companyApiClients.settingsClient.getEmailBidSettings(),
                await this.companyApiClients.settingsClient.getEmailBidSettingParams(),
                await this.companyApiClients.settingsClient.getBidSmtpSettings(),
                await this.companyApiClients.tenantDataProviderClient.getVerifiedDomains()
            ];

            this.params = params;
            this.verifiedDomains = verifiedDomains || [];
            const smtpIntegrationId = bidSmtp?.smtpIntegrationId;
            if (smtpIntegrationId != null && smtpIntegrationId > 0) {
                try {
                    const integration = await this.companyApiClients.companyIntegrationClient.getById(smtpIntegrationId);
                    this.isVerifiedSmtpSender = (integration?.type ?? (integration as any)?.Type) === CompanyIntegrationType.VerifiedSmtpSender;
                } catch {
                    this.isVerifiedSmtpSender = false;
                }
            } else {
                this.isVerifiedSmtpSender = false;
            }

            this.formEmailBidSettingsState.$.subjectTemplate.value = settings?.subjectTemplate;
            this.formEmailBidSettingsState.$.bodyTemplate.value = EditorState.createWithContent(ContentState.createFromText(settings?.bodyTemplate || ''));
            this.formEmailBidSettingsState.$.fromDisplayTemplate.value = settings?.fromDisplayTemplate;
            this.formEmailBidSettingsState.$.fromEmail.value = settings?.fromEmail;
            this.formEmailBidSettingsState.$.ccEmail.value = settings?.cCEmail;
        });
    }
}
